import { type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { Clock, AlertTriangle } from "lucide-react";
import { useBetaInformation } from "@/hooks/queries/useBetaInformation";
import { formatDate } from "@/lib/format";
import { authClient } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { GateCard } from "./GateCard";

// Única ruta que el gate de vencimiento deja pasar: es donde se renueva. Taparla
// dejaría al usuario vencido sin forma de volver a pagar.
const BILLING_PATH = "/settings/plan";

function GateScreen({
  icon,
  iconClass,
  title,
  description,
  note,
  action,
}: {
  icon: ReactNode;
  iconClass: string;
  title: string;
  description: string;
  note?: ReactNode;
  action?: ReactNode;
}) {
  async function signOut() {
    await authClient.signOut();
    window.location.href = "/auth/login";
  }
  return (
    <GateCard title="Acceso a Licitigo">
      <div className="flex flex-col items-center gap-4 py-2 text-center">
        <div
          className={`flex h-16 w-16 items-center justify-center rounded-2xl ${iconClass}`}
        >
          {icon}
        </div>
        <h2 className="text-2xl font-bold tracking-tight">{title}</h2>
        <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>
        {note && <p className="text-xs text-muted-foreground">{note}</p>}
        {action}
        <Button variant="outline" className="mt-2" onClick={signOut}>
          Cerrar sesión
        </Button>
      </div>
    </GateCard>
  );
}

export function AccessGate({ children }: { children: ReactNode }) {
  const { data: info, isLoading, isError } = useBetaInformation();
  const { pathname } = useLocation();

  // El modal de acceso se muestra como overlay sobre la app, replicando el front
  // legado: la interfaz queda atenuada/desenfocada detrás. Mientras carga, o si
  // no podemos leer la info de beta (fail-open), no se muestra ningún gate.
  const gate = (() => {
    if (isLoading || isError || !info) return null;

    if (info.betaexpired && pathname !== BILLING_PATH) {
      const expiredOn = info.plan?.expiresat
        ? ` el ${formatDate(info.plan.expiresat)}`
        : "";
      return (
        <GateScreen
          icon={<AlertTriangle className="h-7 w-7" />}
          iconClass="bg-amber-100 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400"
          title="Tu acceso venció"
          description={`Tu plan ${info.plan?.name ?? ""} venció${expiredOn}. Elige un plan para seguir usando Licitigo.`}
          action={
            <Button asChild className="mt-1">
              <Link to={BILLING_PATH}>Ver planes</Link>
            </Button>
          }
        />
      );
    }

    if (!info.userextrainformation_betaaccepted) {
      return (
        <GateScreen
          icon={<Clock className="h-7 w-7" />}
          iconClass="bg-primary/10 text-primary"
          title="Cuenta en evaluación"
          description="Tu información ha sido recibida correctamente. Nuestro equipo está revisando tu perfil para habilitar el acceso a la plataforma lo antes posible."
          note="Te notificaremos vía correo electrónico cuando seas aceptado."
        />
      );
    }

    return null;
  })();

  return (
    <>
      {children}
      {gate}
    </>
  );
}
